import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { showToast } from 'hfs-common-ui';
import { AppState } from '~/reducers';
import {
  MissionSettingsOperationPayload,
  setResultNotificationSent,
} from '~/domain/assets/store/hfs-operations/reducer';
import { UpdateMissionTypeEnum } from '~/domain/roboticmower-details/models/UpdateMissionTypeEnum';
import type { Operation } from '~/domain/assets/models/Operation';
import { useGetRoboticMowerMissionsQuery } from '~/domain/robotic-mowers/api/robotic-mowers-api';
import { InternationalizationNameSpace } from '~/constants/internationalization-namespace';

interface Params {
  update: MissionSettingsOperationPayload;
}

export const mapLangKeys = (operation: Operation) => {
  if (operation.hfsActionId === UpdateMissionTypeEnum.UpdateMissionBehaviour) {
    return {
      title: 'area_settings_updated_feedback_title',
      description: 'area_settings_updated_feedback_content',
    };
  }
  if (operation.hfsActionId === UpdateMissionTypeEnum.UpdateMissionCuttingHeight) {
    return {
      title: 'area_cutting_height_updated_feedback_title',
      description: 'area_cutting_height_updated_feedback_content',
    };
  }
  if (operation.hfsActionId === UpdateMissionTypeEnum.UpdateMissionSchedule) {
    return {
      title: 'area_schedule_updated_feedback_title',
      description: 'area_schedule_updated_feedback_content',
    };
  }
};

const MissionSettingsUpdateSuccessNotification = ({ update }: Params) => {
  const { data: response } = useGetRoboticMowerMissionsQuery(update.iprId);
  const { t } = useTranslation([InternationalizationNameSpace.RoboticMowerDetails]);
  const dispatch = useDispatch();
  const missions = response?.missions ?? [];
  const operations = useSelector((state: AppState) => state.assetHfsOperations.operations);
  const operation = operations.find((op) => op.operationId === update.operationId);
  const areaName = missions.find((mission) => mission.missionId === update.missionId)?.name;

  useEffect(() => {
    if (areaName !== undefined && operation?.operationId !== undefined) {
      const langKeys = mapLangKeys(operation);
      if (langKeys !== undefined) {
        showToast({
          message: (
            <Trans
              ns={InternationalizationNameSpace.RoboticMowerDetails}
              i18nKey={langKeys.description}
              values={{ areaName }}
            />
          ) as unknown as string,
          title: t(langKeys.title),
          icon: 'action.check',
          iconColor: 'green500',
          position: 'top-right',
          autoClose: 5000,
        });
        dispatch(setResultNotificationSent(operation.operationId));
      }
    }
  }, [areaName, dispatch, operation, t]);

  return undefined;
};

export { MissionSettingsUpdateSuccessNotification };
